import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TemplatePreview } from "@/components/TemplatePreview";
import { Plus, Pencil, Trash2, Lock, Copy } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  name: "", description: "", prompt: "",
  style: { font_size: 86, primary_color: "#FFFFFF", highlight_color: "#FFD400", position: "bottom", uppercase: true, look: "bold", font_family: "Anton", words_per_line: 3, motion: "highlight", layout: "blur", hook_enabled: true },
};

export function TemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = (preset) => {
    setForm(preset ? { name: `${preset.name} custom`, description: preset.description, prompt: preset.prompt, style: { ...preset.style } } : emptyForm);
    setEditId(null); setOpen(true);
  };
  const openEdit = (t) => {
    setForm({ name: t.name, description: t.description || "", prompt: t.prompt || "", style: { ...emptyForm.style, ...t.style } });
    setEditId(t.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give your template a name."); return; }
    setBusy(true);
    try {
      if (editId) await api.put(`/templates/${editId}`, form);
      else await api.post("/templates", form);
      toast.success(editId ? "Template updated" : "Template created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Could not save template.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/templates/${id}`); toast.success("Template deleted"); load(); }
    catch { toast.error("Could not delete."); }
  };

  const setStyle = (k, v) => setForm((f) => ({ ...f, style: { ...f.style, [k]: v } }));

  return (
    <div data-testid="template-manager">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold">Templates</h2>
          <p className="text-sm text-copy-400 mt-1">Five distinct editing styles. Make any look your own without starting from scratch.</p>
        </div>
        <Button data-testid="new-template-btn" onClick={() => openNew()} className="rounded-full bg-cyan-500 text-black hover:bg-cyan-400 font-semibold">
          <Plus className="h-4 w-4 mr-1.5" /> New template
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {templates.map((t) => (
          <div key={t.id} className="rounded-2xl border border-surface-800 bg-panel p-3" data-testid={`template-${t.id}`}>
            <div className="rounded-lg overflow-hidden mb-4"><TemplatePreview template={t} testId={`manager-preview-${t.id}`} /></div>
            <div className="flex items-start justify-between">
              {t.is_default ? (
                <span className="text-[10px] uppercase tracking-widest text-copy-600 flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Default
                </span>
              ) : (
                <div className="flex gap-1">
                  <button data-testid={`edit-template-${t.id}`} onClick={() => openEdit(t)}
                    className="h-8 w-8 rounded-lg hover:bg-surface-800 flex items-center justify-center text-copy-400">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button data-testid={`delete-template-${t.id}`} onClick={() => remove(t.id)}
                    className="h-8 w-8 rounded-lg hover:bg-surface-800 flex items-center justify-center text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <h3 data-testid={`template-name-${t.id}`} className="font-display font-semibold mt-3">{t.name}</h3>
            {t.best_for && <p data-testid={`template-use-${t.id}`} className="text-xs text-cyan-400 mt-1">{t.best_for}</p>}
            <p className="text-sm text-copy-400 mt-2">{t.description || t.prompt}</p>
            <div className="flex gap-2 mt-3 text-[10px] text-copy-400">
              <span className="px-2 py-0.5 rounded-full bg-surface-900 border border-surface-800">{t.style?.position}</span>
              <span className="px-2 py-0.5 rounded-full bg-surface-900 border border-surface-800">{t.style?.uppercase ? "UPPERCASE" : "Sentence"}</span>
              <span className="px-2 py-0.5 rounded-full bg-surface-900 border border-surface-800">{t.style?.font_size}px</span>
            </div>
            <Button data-testid={`clone-template-${t.id}`} variant="outline" onClick={() => openNew(t)} className="mt-4 w-full border-surface-700 bg-transparent hover:bg-surface-800 text-xs">
              <Copy className="w-3 h-3 mr-2" /> Customize this look
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface-950 border-surface-800 text-foreground sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editId ? "Edit template" : "New template"}</DialogTitle>
            <DialogDescription className="text-sm text-copy-500">Define how the AI should pick clips and style captions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-copy-300">Name</Label>
              <Input data-testid="template-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="My viral style" className="bg-surface-900 border-surface-800 focus-visible:ring-cyan-500" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-copy-300">Description</Label>
              <Input data-testid="template-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short description" className="bg-surface-900 border-surface-800 focus-visible:ring-cyan-500" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-copy-300">AI prompt — how to pick & frame clips</Label>
              <Textarea data-testid="template-prompt-input" value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })}
                rows={3} placeholder="e.g. Focus on emotional storytelling moments and controversial hot takes."
                className="bg-surface-900 border-surface-800 focus-visible:ring-cyan-500" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-copy-300">Caption position</Label>
                <Select value={form.style.position} onValueChange={(v) => setStyle("position", v)}>
                  <SelectTrigger data-testid="template-position-select" className="bg-surface-900 border-surface-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface-900 border-surface-800 text-foreground">
                    <SelectItem value="top">Top</SelectItem>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="bottom">Bottom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-copy-300">Font size · {form.style.font_size}px</Label>
                <Slider data-testid="template-size-slider" value={[form.style.font_size]} min={44} max={110} step={2}
                  onValueChange={([v]) => setStyle("font_size", v)} className="mt-3" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-copy-300">Text color</Label>
                <input data-testid="template-color-input" type="color" value={form.style.primary_color} onChange={(e) => setStyle("primary_color", e.target.value)}
                  className="h-10 w-full rounded-lg bg-surface-900 border border-surface-800 cursor-pointer" />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-surface-800 bg-surface-900 px-3">
                <Label className="text-copy-300">UPPERCASE</Label>
                <Switch data-testid="template-uppercase-switch" checked={form.style.uppercase} onCheckedChange={(v) => setStyle("uppercase", v)}
                  className="data-[state=checked]:bg-cyan-500" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-copy-300">Highlight color</Label>
                <input data-testid="template-highlight-color" type="color" value={form.style.highlight_color} onChange={e => setStyle("highlight_color", e.target.value)} className="h-10 w-full rounded-lg bg-surface-900 border border-surface-800" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-copy-300">Caption motion</Label>
                <Select value={form.style.motion || "highlight"} onValueChange={v => setStyle("motion", v)}>
                  <SelectTrigger data-testid="template-motion-select" className="bg-surface-900 border-surface-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface-900 border-surface-800 text-foreground">
                    <SelectItem data-testid="template-motion-highlight" value="highlight">Word highlight</SelectItem>
                    <SelectItem data-testid="template-motion-pop" value="pop">Pop entrance</SelectItem>
                    <SelectItem data-testid="template-motion-fade" value="fade">Quiet phrases</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="save-template-btn" onClick={save} disabled={busy}
              className="w-full rounded-full bg-cyan-500 text-black hover:bg-cyan-400 font-semibold h-11">
              {busy ? "Saving…" : editId ? "Save changes" : "Create template"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
